import { Combobox, Input, useCombobox } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { apiClient } from "~/api/ApiClient";
import { ApiError } from "~/api/types";
import Modal from "~/components/ui/modal/Modal.tsx";
import { useMediaQuery } from "~/hooks/useMediaQuery";

import { Ids, PageBulkRecrawlAttributes } from "../../../api/Api";
import { Icon } from "../../../assets/icons/Icon";
import Button from "../../mantine/button/Button";
import filterStyles from "./PageFilters.module.scss";
import styles from "./PageTable.module.scss";

type ComboboxReturnType = ReturnType<typeof useCombobox>;

interface RecrawlComboboxProps {
  selectedIds: Ids;
  selectedIdsToRecrawl: number[];
  selectedIdsToIgnore: number[];
  hasMixedIgnoreStatuses: boolean;
  canSoftCrawl: boolean;
  canForceRecrawl: boolean;
  canAbortCrawl: boolean;
  recrawlPagesBulk: (ids: PageBulkRecrawlAttributes) => void;
  openBulkIgnoreModal: () => void;
  openCrawlModal: (type: "soft" | "force") => void;
  setSelectedIds: (ids: Ids) => void;
  combobox: ComboboxReturnType;
  isOpened: boolean;
  setIsOpened: (isOpened: boolean) => void;
}

const RecrawlCombobox = ({
  selectedIds,
  selectedIdsToRecrawl,
  selectedIdsToIgnore,
  hasMixedIgnoreStatuses,
  canSoftCrawl,
  canForceRecrawl,
  canAbortCrawl,
  recrawlPagesBulk,
  openBulkIgnoreModal,
  openCrawlModal,
  setSelectedIds,
  combobox,
  isOpened,
  setIsOpened,
}: RecrawlComboboxProps) => {
  const [isAbortCrawlModalOpened, setIsAbortCrawlModalOpened] =
    useState<boolean>(false);
  const { id: siteId } = useParams();

  // Matches the breakpoint in PageFilters.module.scss that hides the button
  // label. Tooltip is only useful in the compact state where the button shows
  // just the icon without text.
  const isCompact = useMediaQuery(
    `(max-width: ${filterStyles.compactBreakpoint})`,
  );

  const { mutate: abortCrawl } = useMutation({
    mutationFn: async () => {
      await apiClient.sites.abortCrawlCreate(Number(siteId));
    },
    onError: async (error: ApiError) => {
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Your request to cancel the crawl has been received.", {
        variant: "success",
      });
    },
  });

  return (
    <Combobox store={combobox} withinPortal={false}>
      <Combobox.Target>
        <Button
          component="button"
          type="button"
          variant="outlined"
          onClick={() => combobox.toggleDropdown()}
          style={{ textAlign: "left" }}
          leftSection={<Icon iconName="BoltIcon" />}
          {...(isCompact && {
            "data-tooltip-id": "page-filters-tooltip",
            "data-tooltip-content": "Site Actions",
          })}
          rightSection={<Icon iconName={isOpened ? "IconUp" : "IconDown"} />}
        >
          <Input.Placeholder>
            <span className={filterStyles.btnLabel}>
              {selectedIds.length > 0
                ? `${selectedIds.length} Pages Selected`
                : "Site Actions"}
            </span>
          </Input.Placeholder>
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown className={styles.selectFilterDropdown}>
        <Combobox.Options>
          {selectedIds.length > 0 ? (
            <>
              <Combobox.Option value="bulkRecrawl">
                <Button
                  variant="menuItem"
                  disabled={selectedIdsToRecrawl.length === 0}
                  onClick={() => {
                    if (selectedIdsToRecrawl.length === 0) return;
                    recrawlPagesBulk({ ids: selectedIdsToRecrawl });
                    combobox.resetSelectedOption();
                    setSelectedIds([]);
                    setIsOpened(false);
                    combobox.toggleDropdown();
                  }}
                >
                  Recrawl Selected ({selectedIdsToRecrawl.length})
                </Button>
              </Combobox.Option>
              <Combobox.Option value="bulkIgnore">
                <div className={styles.bulkActionRow}>
                  {hasMixedIgnoreStatuses ? (
                    <div className={styles.bulkActionUnavailable}>
                      Unavailable for mixed statuses
                    </div>
                  ) : (
                    <span className={styles.bulkActionButtonWrapper}>
                      <Button
                        variant="menuItem"
                        disabled={selectedIdsToIgnore.length === 0}
                        onClick={() => {
                          if (selectedIdsToIgnore.length === 0) return;
                          openBulkIgnoreModal();
                          combobox.toggleDropdown();
                        }}
                      >
                        Toggle ignore/noindex ({selectedIdsToIgnore.length})
                      </Button>
                    </span>
                  )}
                </div>
              </Combobox.Option>
            </>
          ) : (
            <>
              <Combobox.Option value="soft">
                <Button
                  variant="menuItem"
                  disabled={!canSoftCrawl}
                  onClick={() => {
                    openCrawlModal("soft");
                    combobox.toggleDropdown();
                  }}
                >
                  Soft Crawl
                </Button>
              </Combobox.Option>
              <Combobox.Option value="force">
                <Button
                  variant="menuItem"
                  disabled={!canForceRecrawl}
                  onClick={() => {
                    openCrawlModal("force");
                    combobox.toggleDropdown();
                  }}
                >
                  Force Recrawl
                </Button>
              </Combobox.Option>

              {canAbortCrawl && (
                <>
                  <Combobox.Option value="abortCrawl">
                    <Button
                      variant="menuItem"
                      onClick={() => {
                        setIsAbortCrawlModalOpened(true);
                        combobox.toggleDropdown();
                      }}
                    >
                      End Crawl
                    </Button>
                  </Combobox.Option>

                  <Modal
                    isOpen={isAbortCrawlModalOpened}
                    closeModal={() => setIsAbortCrawlModalOpened(false)}
                    modalLabel={
                      <h3 className="">You are about to end the site crawl</h3>
                    }
                    buttonLabel="End"
                    action={abortCrawl}
                  >
                    <p>Are you sure?</p>
                  </Modal>
                </>
              )}
            </>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default RecrawlCombobox;
